import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useDebouncedCallback } from 'use-debounce';

import AdminHeader from '../../../components/header/AdminHeader';
import Pagination from '../../../components/form/Pagination';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';
import CallexLoader from '../../../components/CallexLoader';
import { checkModulePermission } from '../../../utils/common';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import {
  botRagListGet,
  botRagDelete,
  useBotRagList
} from '../../../store/slices/tenantSlices/configurationSlice';
import AddBotRagModal from './AddBotRagModal';

const PERMISSION_KEY = 'Bot RAG';

const BotRag = () => {
  const dispatch = useDispatch();
  const botRagList = useBotRagList();
  const userRolePermission = useUserRolePermission();

  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10
  });

  const fetchBotRagList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      await dispatch(botRagListGet({ ...filter })).unwrap();
    } catch (error) {
      console.error('Error fetching RAG files:', error);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  useEffect(() => {
    fetchBotRagList();
  }, [filter]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await dispatch(botRagDelete(isDeleteId)).unwrap();
      if (response && response.success) {
        toast.success(response.message || 'RAG file deleted successfully');
        if (botRagList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchBotRagList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.error('Error deleting RAG file:', error);
      toast.error('Failed to delete RAG file');
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleAddModal = () => setIsAddModalOpen(!isAddModalOpen);

  return (
    <div>
      <AdminHeader
        title="Bot RAG"
        isAdd
        onAddClick={toggleAddModal}
        isAddDisabled={!checkModulePermission('is_add', PERMISSION_KEY, userRolePermission)}
        addBtnName="Add RAG"
      />

      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : botRagList?.data?.length > 0 &&
        checkModulePermission('is_view', PERMISSION_KEY, userRolePermission) ? (
        <div className="p-0 position-relative m-0">
          <Card className="p-0 position-relative m-0">
            <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
              <table className="report-list-table">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>File Name</th>
                    <th className="text-center" style={{ width: '90px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {botRagList?.data?.map((item) => (
                    <tr key={item.id}>
                      <td>{item.bot_name || '-'}</td>
                      <td>{item.file_name || '-'}</td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission(
                                'is_delete',
                                PERMISSION_KEY,
                                userRolePermission
                              ) && 'callex-disabled'
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission(
                                    'is_delete',
                                    PERMISSION_KEY,
                                    userRolePermission
                                  )
                                ) {
                                  setIsDeleteId(item.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={botRagList?.total}
              limit={botRagList?.limit}
              page={botRagList?.page}
              onLimitChange={(val) => setFilter({ ...filter, page: 1, limit: val })}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchBotRagList()}
            />
          </div>
        </div>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}

      <AddBotRagModal
        isOpen={isAddModalOpen}
        toggle={toggleAddModal}
        refreshList={fetchBotRagList}
      />

      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default BotRag;
